import { useEffect, useState } from "react";
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Chip, CircularProgress, Alert, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArchiveIcon from "@mui/icons-material/Archive";
import { budgetService } from "@/api/budget.service";
import type { MonthlyHistory, BudgetBreakdown } from "@/api/types";

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const fmtDate = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

function Row({ label, value, bold, color }: { label: string; value: string; bold?: boolean; color?: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} color={color}>{value}</Typography>
    </Box>
  );
}

export default function HistoryPage() {
  const [history, setHistory] = useState<MonthlyHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    budgetService.getHistory().then(setHistory).catch(e => setError(e.response?.data?.error || "Erreur.")).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Historique</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {history.length === 0 && <Alert severity="info">Aucun historique. Archivez un mois depuis le tableau de bord.</Alert>}
      {history.map(h => (
        <Accordion key={`${h.year}-${h.month}`} sx={{ mb: 1, borderRadius: "12px !important", "&:before": { display: "none" } }} elevation={1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
              <Typography fontWeight={600}>{MONTHS[h.month - 1]} {h.year}</Typography>
              {h.payment ? (
                <Chip
                  icon={<CheckCircleOutlineIcon />}
                  label={`Réglé — ${fmt(h.payment.amount)}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  icon={<SwapHorizIcon />}
                  label="Non réglé"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Archive date */}
            {h.archivedAt && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                <ArchiveIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                <Typography variant="caption" color="text.secondary">
                  Archivé le {fmtDate(h.archivedAt)}
                </Typography>
              </Box>
            )}

            {/* Payment info */}
            {h.payment && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  borderColor: "success.dark",
                  background: "rgba(46,125,50,0.08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2">
                  <Box component="span" fontWeight={700}>{h.payment.fromName}</Box>
                  {" a versé "}
                  <Box component="span" fontWeight={700} color="success.main">{fmt(h.payment.amount)}</Box>
                  {" à "}
                  <Box component="span" fontWeight={700}>{h.payment.toName}</Box>
                </Typography>
              </Paper>
            )}

            {/* Per-person breakdown */}
            {h.breakdown.map((b: BudgetBreakdown) => (
              <Box key={b.userId} sx={{ mb: 2 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>{b.name} <Chip label={b.incomeRatio} size="small" sx={{ ml: 1 }} /></Typography>
                <Row label="Revenu" value={fmt(b.income)} />
                <Row label="Charges payées" value={fmt(b.sharedActuallyPaid ?? 0)} />
                <Row label="Part équitable" value={fmt(b.sharedContribution)} />
                <Row label="Dépenses perso" value={fmt(b.personalExpenses)} />
                <Divider sx={{ my: 1 }} />
                <Row label="Total à payer" value={fmt(b.totalToPay)} bold />
                <Row label="Reste" value={fmt(b.remainingAfterExpenses)} color={b.remainingAfterExpenses >= 0 ? "success.main" : "error.main"} />
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
